const API_BASE = import.meta.env.PROD ? "" : "http://localhost:8787";

function getApiKey(): string {
  return localStorage.getItem("pbookmarks_api_key") || "";
}

export interface Bookmark {
  id: string;
  url: string;
  title: string;
  description?: string;
  notes?: string;
  highlight?: string;
  tags?: string;
  isArchived: boolean;
  createdAt: Date;
  updatedAt: Date;
  lastAccessed?: Date;
  isRead: boolean;
  readLater: boolean;
  faviconUrl?: string;
  urlValidatedAt?: Date;
  urlValidationStatus?: "valid" | "invalid" | "pending" | "unknown";
}

export interface Tag {
  id: number;
  name: string;
  createdAt: Date;
}

export interface ApiResponse<T> {
  data: T;
  meta?: {
    limit?: number;
    offset?: number;
    total?: number;
    count?: number;
  };
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": getApiKey(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Unknown error" }));
      throw new Error(error.error || "Request failed");
    }

    return response.json();
  }

  async getBookmarks(params?: {
    search?: string;
    tag?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<Bookmark[]>> {
    const query = new URLSearchParams();
    if (params?.search) query.set("search", params.search);
    if (params?.tag) query.set("tag", params.tag);
    if (params?.limit) query.set("limit", params.limit.toString());
    if (params?.offset) query.set("offset", params.offset.toString());

    const queryStr = query.toString();
    return this.request<ApiResponse<Bookmark[]>>(
      `/api/bookmarks${queryStr ? `?${queryStr}` : ""}`
    );
  }

  async getBookmark(id: string): Promise<ApiResponse<Bookmark>> {
    return this.request<ApiResponse<Bookmark>>(`/api/bookmarks/${id}`);
  }

  async createBookmark(data: {
    url: string;
    title: string;
    description?: string;
    notes?: string;
    tags?: string[];
  }): Promise<ApiResponse<Bookmark>> {
    return this.request<ApiResponse<Bookmark>>("/api/bookmarks", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateBookmark(
    id: string,
    data: Partial<{
      url: string;
      title: string;
      description: string;
      notes: string;
      tags: string[] | undefined;
      readLater: boolean;
      isRead: boolean;
    }>
  ): Promise<ApiResponse<Bookmark>> {
    return this.request<ApiResponse<Bookmark>>(`/api/bookmarks/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteBookmark(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/api/bookmarks/${id}`, {
      method: "DELETE",
    });
  }

  async getTags(): Promise<ApiResponse<Tag[]>> {
    return this.request<ApiResponse<Tag[]>>("/api/tags");
  }

  async createTag(name: string): Promise<ApiResponse<Tag>> {
    return this.request<ApiResponse<Tag>>("/api/tags", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  }

  async deleteTag(id: number): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/api/tags/${id}`, {
      method: "DELETE",
    });
  }

  async search(query: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<Bookmark[]>> {
    const queryParams = new URLSearchParams({ q: query });
    if (params?.limit) queryParams.set("limit", params.limit.toString());
    if (params?.offset) queryParams.set("offset", params.offset.toString());

    return this.request<ApiResponse<Bookmark[]>>(
      `/api/search?${queryParams.toString()}`
    );
  }

  async archiveBookmark(id: string): Promise<{
    success: boolean;
    archiveKey: string;
    size: number;
    contentType: string;
  }> {
    return this.request<{
      success: boolean;
      archiveKey: string;
      size: number;
      contentType: string;
    }>(`/api/bookmarks/${id}/archive`, {
      method: "POST",
    });
  }

  async getArchivedContent(id: string): Promise<{
    content: string;
    metadata: {
      url: string;
      title: string;
      archivedAt: string;
      contentType: string;
      size: number;
    };
    archiveKey: string;
  }> {
    return this.request<{
      content: string;
      metadata: {
        url: string;
        title: string;
        archivedAt: string;
        contentType: string;
        size: number;
      };
      archiveKey: string;
    }>(`/api/bookmarks/${id}/archive`);
  }

  async exportBookmarks(params?: {
    format?: "json" | "pinboard";
    tag?: string;
    fromDate?: string;
    toDate?: string;
  }): Promise<{
    bookmarks: Bookmark[];
    exportedAt?: string;
  }> {
    const query = new URLSearchParams();
    if (params?.format) query.set("format", params.format);
    if (params?.tag) query.set("tag", params.tag);
    if (params?.fromDate) query.set("fromDate", params.fromDate);
    if (params?.toDate) query.set("toDate", params.toDate);
    
    const queryStr = query.toString();
    return this.request<{
      bookmarks: Bookmark[];
      exportedAt?: string;
    }>(`/api/export${queryStr ? `?${queryStr}` : ""}`);
  }

  async importBookmarks(bookmarks: Array<{
    href: string;
    description: string;
    extended?: string;
    notes?: string;
    tags?: string[];
    time?: string;
  }>): Promise<{
    success: boolean;
    imported: number;
    skipped: number;
  }> {
    return this.request<{
      success: boolean;
      imported: number;
      skipped: number;
    }>("/api/import", {
      method: "POST",
      body: JSON.stringify({ bookmarks }),
    });
  }

  async importBookmarksHtml(html: string): Promise<{
    success: boolean;
    imported: number;
    skipped: number;
  }> {
    const response = await fetch(`${this.baseUrl}/api/import/html`, {
      method: "POST",
      headers: {
        "Content-Type": "text/html",
        "X-API-Key": getApiKey(),
      },
      body: html,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Unknown error" }));
      throw new Error(error.error || "Import failed");
    }

    return response.json();
  }

  async validateBookmark(id: string): Promise<ApiResponse<Bookmark>> {
    return this.request<ApiResponse<Bookmark>>(`/api/bookmarks/${id}/validate`, {
      method: "POST",
    });
  }

  async refreshBookmark(id: string): Promise<ApiResponse<Bookmark>> {
    return this.request<ApiResponse<Bookmark>>(`/api/bookmarks/${id}/refresh`, {
      method: "POST",
    });
  }
}

export const api = new ApiClient(API_BASE);
